import {
  ComponentProps,
  ComponentType,
  ForwardRefRenderFunction,
  Key,
  ReactElement,
  Suspense,
  forwardRef,
  lazy,
  memo,
} from "react";
import { getKey } from "./key";

export type ComponentWrapper = (inner: ReactElement) => ReactElement;

export function composeWrappers(
  ...wrappers: ComponentWrapper[]
): ComponentWrapper {
  return (inner) =>
    wrappers.reduceRight((result, wrapper) => wrapper(result), inner);
}

export function Component({ children }: { children(): ReactElement }) {
  return children();
}

export function IterableMap<T>({
  children,
  keyFn = getKey,
  items,
}: {
  children(item: T, index: number): ReactElement;
  keyFn?(item: T, index: number): Key;
  items: Iterable<T>;
}) {
  return (
    <>
      {Array.from(items, (item, index) => (
        <Component key={keyFn(item, index)}>
          {() => children(item, index)}
        </Component>
      ))}
    </>
  );
}

export function genericMemo<T extends ComponentType<any>>(
  Component: T,
  propsAreEqual?: (
    prevProps: Readonly<ComponentProps<T>>,
    nextProps: Readonly<ComponentProps<T>>,
  ) => boolean,
): T {
  return memo(Component, propsAreEqual) as any;
}

export function genericForwardRefMemo<
  T extends ForwardRefRenderFunction<any, any>,
>(Component: T): T {
  return memo(forwardRef(Component as any)) as any;
}

/**
 * Lazily-loaded component.
 */
export function lazyComponent<T extends ComponentType<any>>(
  componentFn: () => Promise<T>,
): T {
  const Component = lazy(async () => ({ default: await componentFn() })) as any;
  return forwardRef(function LazyComponent(props, ref) {
    // Suspense, otherwise: Error: A component suspended while responding to synchronous input.
    return (
      <Suspense>
        <Component {...props} ref={ref} />
      </Suspense>
    );
  }) as any;
}
